import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './ScenarioGenerator.module.css';

// 呼び出し側が options を渡さないときの最終フォールバック。モデルの supported_durations
// が未取得な間の表示用で、汎用フォールバックレンジ (backend scenario_generator の
// フォールバック 4〜10秒) と揃える。
const DURATION_OPTIONS: { value: number; label: string; shortLabel?: string }[] = [
  { value: 4, label: '4秒' },
  { value: 5, label: '5秒' },
  { value: 6, label: '6秒' },
  { value: 7, label: '7秒' },
  { value: 8, label: '8秒' },
  { value: 9, label: '9秒' },
  { value: 10, label: '10秒' },
];

export { DURATION_OPTIONS };

/** 尺セレクト（カスタムドロップダウン）。options を渡せばその一覧に差し替える。 */
export function DurationSelect({
  value,
  onChange,
  disabled = false,
  options,
  formatFallback,
}: {
  value: number;
  onChange: (v: number) => void;
  disabled?: boolean;
  /** shortLabel: トリガー (閉じた状態) 用の短い表示。未指定は label をそのまま使う */
  options?: { value: number; label: string; shortLabel?: string }[];
  /** 現在値が options に無いときの表示。未指定は尺用の「N秒」(シーン番号等では上書きする) */
  formatFallback?: (v: number) => string;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  // ドロップダウンの最大高さ。共通クラス (.voiceDropdown) の 240px だと 8 件ほどしか
  // 見えず、シーン番号のように選択肢が多いとスクロールに気付きにくい。開いた位置から
  // 画面下端までを上限にして、入る分だけ一覧を伸ばす (下端に近いときは従来の 240px)。
  const [maxHeight, setMaxHeight] = useState<number>(240);
  const triggerRef = useRef<HTMLDivElement>(null);

  const items = options ?? DURATION_OPTIONS;
  const selected = items.find(o => o.value === value);

  const handleOpen = () => {
    if (disabled) return;
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const top = rect.bottom + 4;
      setPos({ top, left: rect.left });
      setMaxHeight(Math.max(240, window.innerHeight - top - 16));
    }
    setOpen(true);
  };

  return (
    <>
      <div
        ref={triggerRef}
        className={styles.voiceSelectTrigger}
        onClick={() => disabled ? undefined : open ? setOpen(false) : handleOpen()}
        style={disabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
      >
        <span className={styles.voiceSelectLabel}>{selected?.shortLabel ?? selected?.label ?? (formatFallback ? formatFallback(value) : `${value}秒`)}</span>
        <span className={styles.voiceSelectArrow}><svg width="8" height="6" viewBox="0 0 8 6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 1l3 4 3-4" /></svg></span>
      </div>
      {open && createPortal(
        <>
          <div className={styles.voiceDropdownOverlay} onClick={() => setOpen(false)} />
          <div
            className={`modal-surface ${styles.voiceDropdown}`}
            style={{ position: 'fixed', top: pos.top, left: pos.left, width: 'auto', maxHeight }}
          >
            {items.map(o => (
              <div
                key={o.value}
                className={`${styles.voiceDropdownItem} ${o.value === value ? styles.voiceDropdownItemActive : ''}`}
                onClick={() => { onChange(o.value); setOpen(false); }}
              >
                <span className={styles.voiceDropdownName}>{o.label}</span>
              </div>
            ))}
          </div>
        </>,
        document.body,
      )}
    </>
  );
}

/**
 * 尺ステッパー。秒数表示の右側にシェブロン風の上下ボタンを置き、
 * options の範囲内で 1 段ずつ秒数を増減する。絵コンテのコマ尺調整に使用。
 */
export function DurationStepper({
  value,
  onChange,
  disabled = false,
  options,
}: {
  value: number;
  onChange: (v: number) => void;
  disabled?: boolean;
  options?: { value: number; label: string }[];
}) {
  const items = options ?? DURATION_OPTIONS;
  // 選択肢を値の昇順に並べ、現在値の前後の候補を求める。
  const sorted = [...items].sort((a, b) => a.value - b.value);
  const idx = sorted.findIndex(o => o.value === value);
  const prev = idx > 0 ? sorted[idx - 1] : undefined;
  const next = idx >= 0 && idx < sorted.length - 1 ? sorted[idx + 1] : undefined;
  const selected = sorted.find(o => o.value === value);

  return (
    <div
      className={styles.durationStepper}
      style={disabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
    >
      <span className={styles.durationStepperValue}>{selected?.label ?? `${value}秒`}</span>
      <div className={styles.durationStepperButtons}>
        <button
          type="button"
          className={styles.durationStepperButton}
          onClick={() => next && onChange(next.value)}
          disabled={disabled || !next}
          aria-label="尺を伸ばす"
        >
          <svg width="9" height="6" viewBox="0 0 8 6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 5l3-4 3 4" /></svg>
        </button>
        <button
          type="button"
          className={styles.durationStepperButton}
          onClick={() => prev && onChange(prev.value)}
          disabled={disabled || !prev}
          aria-label="尺を縮める"
        >
          <svg width="9" height="6" viewBox="0 0 8 6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 1l3 4 3-4" /></svg>
        </button>
      </div>
    </div>
  );
}

/**
 * 数値ステッパー。DurationStepper と同じ控えめな見た目で、min〜max の範囲内を
 * step 刻みで増減する自由数値版。BGM 長さなど離散選択肢を持たない秒数に使用。
 * 値表示をクリックすると自由入力フィールドに切り替わり、Enter/フォーカスアウトで確定する。
 */
export function NumberStepper({
  value,
  onChange,
  min = 0,
  max = Number.MAX_SAFE_INTEGER,
  step = 1,
  disabled = false,
  readOnly = false,
  suffix = '',
  placeholder = '—',
  decimals = 0,
}: {
  value: number | null;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  /** 固定値表示モード。ボタンは活性の見た目のまま増減・直接入力を受け付けない */
  readOnly?: boolean;
  suffix?: string;
  placeholder?: string;
  /** 小数点以下の桁数 (既定 0 = 整数)。増減と直接入力の値をこの桁で丸める (0.1 刻みの秒など) */
  decimals?: number;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  // 未設定 (null) のときは min を起点に増減する。
  const current = value ?? min;
  const roundTo = (v: number) => {
    const f = 10 ** decimals;
    return Math.round(v * f) / f;
  };
  const clamp = (v: number) => roundTo(Math.max(min, Math.min(max, v)));

  const startEditing = () => {
    if (disabled || readOnly) return;
    setDraft(value == null ? '' : String(value));
    setEditing(true);
  };

  const commit = () => {
    const n = Number(draft);
    if (draft !== '' && Number.isFinite(n)) {
      onChange(clamp(n));
    }
    setEditing(false);
  };

  return (
    <div
      className={styles.durationStepper}
      style={disabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
    >
      {editing ? (
        <input
          type="number"
          className={styles.durationStepperInput}
          min={min}
          max={max}
          step={step}
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              setEditing(false);
            }
          }}
        />
      ) : (
        <span
          className={styles.durationStepperValue}
          onClick={startEditing}
          style={disabled || readOnly ? undefined : { cursor: 'text' }}
          title={readOnly ? undefined : 'クリックして入力'}
        >
          {value == null ? placeholder : `${value}${suffix}`}
        </span>
      )}
      <div className={styles.durationStepperButtons}>
        <button
          type="button"
          className={styles.durationStepperButton}
          onClick={() => { if (!readOnly) onChange(clamp(current + step)); }}
          disabled={disabled || (!readOnly && current >= max)}
          aria-label="増やす"
        >
          <svg width="9" height="6" viewBox="0 0 8 6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 5l3-4 3 4" /></svg>
        </button>
        <button
          type="button"
          className={styles.durationStepperButton}
          onClick={() => { if (!readOnly) onChange(clamp(current - step)); }}
          disabled={disabled || (!readOnly && current <= min)}
          aria-label="減らす"
        >
          <svg width="9" height="6" viewBox="0 0 8 6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 1l3 4 3-4" /></svg>
        </button>
      </div>
    </div>
  );
}
